import React, { memo, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { Modal } from '@/shared/components/modals';
import { VisualizationViolinDiagramContainer } from '../../VisualizationViolinDiagramContainer/VisualizationViolinDiagramContainer';

interface ViolinPlotsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

/**
 * Modal for displaying pre-generated violin plots
 * Shows the VisualizationViolinDiagramContainer with all violin plot visualizations
 */
export const ViolinPlotsModal: React.FC<ViolinPlotsModalProps> = memo(({
  isOpen,
  onClose,
  sessionId
}) => {
  const { t } = useTranslation('training');
  const titleId = useId();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleId={titleId}
      size="6xl"
      className="ring-1 ring-border/60 animate-slideUp rounded-xl shadow-2xl"
    >
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border flex-shrink-0">
        <h2 id={titleId} className="text-base font-semibold text-foreground tracking-tight">
          {t('training.sidebar.violinPlotsTitle', 'Violin Plots')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          aria-label={t('common.close', 'Close')}
        >
          <FaTimes size={14} />
        </button>
      </div>

      {/* Content - VisualizationViolinDiagramContainer */}
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 65px)' }}>
        <VisualizationViolinDiagramContainer sessionId={sessionId} />
      </div>
    </Modal>
  );
});

ViolinPlotsModal.displayName = 'ViolinPlotsModal';

export default ViolinPlotsModal;
